import React, { useState, useEffect, useRef } from "react";
import { subscriptionService } from "@/services/subscriptionService";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AnimatedBackground } from "@/components/ui/AnimatedBackground";
import {
  Loader2,
  AlertCircle,
  ArrowLeft,
  Check,
  Building,
  User,
  Mail,
  Phone,
  Globe,
  Lock,
  Eye,
  EyeOff,
  Shield,
  ArrowRight,
  Coins,
} from "lucide-react";
import { countries } from "@/data/countries";
import "flag-icons/css/flag-icons.min.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { authAPI } from "@/services/api";
import { CloudLogo } from "@/components/auth/LoginForm";
import loginLogo from "@/components/utils/Logo CRM.png";

const ENTERPRISE_PLAN_ID = "0602e9cb-d257-11f0-93c8-5254005369ba";

export const PlanRegistrationPage: React.FC = () => {
  const navigate = useNavigate();

  const currencyOptions = [
    { name: "Dólar (USD)", symbol: "$" },
    { name: "Euro (EUR)", symbol: "€" },
    { name: "Quetzal (GTQ)", symbol: "Q" },
    { name: "Libra (GBP)", symbol: "£" },
  ];

  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [companyCountrySearch, setCompanyCountrySearch] = useState("");
  const [showCompanyCountryDropdown, setShowCompanyCountryDropdown] =
    useState(false);
  const [companyCountryCode, setCompanyCountryCode] = useState("");

  const [adminCountrySearch, setAdminCountrySearch] = useState("");
  const [showAdminCountryDropdown, setShowAdminCountryDropdown] =
    useState(false);
  const [adminCountryCode, setAdminCountryCode] = useState("");

  const companyCountryRef = useRef<HTMLInputElement>(null);
  const adminCountryRef = useRef<HTMLInputElement>(null);
  const companyCountryContainerRef = useRef<HTMLDivElement>(null);
  const adminCountryContainerRef = useRef<HTMLDivElement>(null);

  // Cerrar los selectores de país al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        companyCountryContainerRef.current &&
        !companyCountryContainerRef.current.contains(event.target as Node)
      ) {
        setShowCompanyCountryDropdown(false);
      }
      if (
        adminCountryContainerRef.current &&
        !adminCountryContainerRef.current.contains(event.target as Node)
      ) {
        setShowAdminCountryDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [formData, setFormData] = useState({
    companyName: "",
    companyEmail: "",
    companyPhone: "",
    website: "",
    currency: "",
    adminFirstName: "",
    adminLastName: "",
    adminEmail: "",
    adminPassword: "",
    confirmPassword: "",
    adminPhone: "",
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const filteredCompanyCountries = countries.filter(
    (c) =>
      c.name.toLowerCase().includes(companyCountrySearch.toLowerCase()) ||
      c.code.includes(companyCountrySearch),
  );

  const filteredAdminCountries = countries.filter(
    (c) =>
      c.name.toLowerCase().includes(adminCountrySearch.toLowerCase()) ||
      c.code.includes(adminCountrySearch),
  );

  const handleSelectCompanyCountry = (country: (typeof countries)[0]) => {
    setCompanyCountryCode(country.code);
    setCompanyCountrySearch(`${country.name} (${country.code})`);
    setShowCompanyCountryDropdown(false);
  };

  const handleSelectAdminCountry = (country: (typeof countries)[0]) => {
    setAdminCountryCode(country.code);
    setAdminCountrySearch(`${country.name} (${country.code})`);
    setShowAdminCountryDropdown(false);
  };

  const validateStep = (step: number): boolean => {
    const errors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.companyName.trim()) {
        errors.companyName = "El nombre de la empresa es requerido";
      } else if (formData.companyName.length < 2) {
        errors.companyName = "El nombre debe tener al menos 2 caracteres";
      }

      if (!formData.companyEmail.trim()) {
        errors.companyEmail = "El email de la empresa es requerido";
      } else if (!/\S+@\S+\.\S+/.test(formData.companyEmail)) {
        errors.companyEmail = "Email no válido";
      }

      if (formData.companyPhone.trim() && !/^[0-9\s+-]+$/.test(formData.companyPhone)) {
        errors.companyPhone = "El teléfono solo debe contener números";
      }

      if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
        errors.website = "URL no válida (debe incluir http:// o https://)";
      }
    } else if (step === 2) {
      if (!formData.adminFirstName.trim()) {
        errors.adminFirstName = "El nombre es requerido";
      } else if (!/^[A-Za-zÁÉÍÓÚáéíóúÑñÜü\s]+$/.test(formData.adminFirstName)) {
        errors.adminFirstName = "El nombre solo debe contener letras";
      }

      if (!formData.adminLastName.trim()) {
        errors.adminLastName = "El apellido es requerido";
      } else if (!/^[A-Za-zÁÉÍÓÚáéíóúÑñÜü\s]+$/.test(formData.adminLastName)) {
        errors.adminLastName = "El apellido solo debe contener letras";
      }

      if (!formData.adminEmail.trim()) {
        errors.adminEmail = "El email es requerido";
      } else if (!/\S+@\S+\.\S+/.test(formData.adminEmail)) {
        errors.adminEmail = "Email no válido";
      }

      if (formData.adminPhone.trim() && !/^[0-9\s+-]+$/.test(formData.adminPhone)) {
        errors.adminPhone = "El teléfono solo debe contener números";
      }

      if (!formData.adminPassword) {
        errors.adminPassword = "La contraseña es requerida";
      } else if (formData.adminPassword.length < 8) {
        errors.adminPassword = "La contraseña debe tener al menos 8 caracteres";
      }

      if (formData.adminPassword !== formData.confirmPassword) {
        errors.confirmPassword = "Las contraseñas no coinciden";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // 1. Filtrar teléfono en tiempo real (solo números, espacios, guiones, más)
    if (name === "companyPhone" || name === "adminPhone") {
      const cleaned = value.replace(/[^0-9\s+-]/g, "");
      setFormData((prev) => ({ ...prev, [name]: cleaned }));
      if (formErrors[name]) {
        setFormErrors((prev) => ({ ...prev, [name]: "" }));
      }
      return;
    }

    // 2. Filtrar nombre y apellido del admin en tiempo real (solo letras, espacios, acentos)
    if (name === "adminFirstName" || name === "adminLastName") {
      const cleaned = value.replace(/[^A-Za-zÁÉÍÓÚáéíóúÑñÜü\s]/g, "");
      setFormData((prev) => ({ ...prev, [name]: cleaned }));
      if (formErrors[name]) {
        setFormErrors((prev) => ({ ...prev, [name]: "" }));
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleNextStep = () => {
    if (validateStep(step)) {
      if (step < 3) {
        setStep(step + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handlePreviousStep = () => {
    if (step > 1) {
      setStep(step - 1);
      setFormErrors({});
    }
  };

  const handleSubmit = async () => {
    try {
      setRegistering(true);
      setError(null);

      const finalCompanyPhone =
        companyCountryCode && formData.companyPhone
          ? `${companyCountryCode} ${formData.companyPhone}`
          : formData.companyPhone;

      const finalAdminPhone =
        adminCountryCode && formData.adminPhone
          ? `${adminCountryCode} ${formData.adminPhone}`
          : formData.adminPhone;

      const subscriptionData = {
        companyName: formData.companyName.trim(),
        companyEmail: formData.companyEmail.trim(),
        companyPhone: finalCompanyPhone?.trim() || undefined,
        website: formData.website?.trim() || undefined,
        currency: formData.currency,
        planId: ENTERPRISE_PLAN_ID,
        ownerFirstName: formData.adminFirstName.trim(),
        ownerLastName: formData.adminLastName.trim(),
        ownerEmail: formData.adminEmail.trim(),
        ownerPassword: formData.adminPassword,
        ownerPhone: finalAdminPhone?.trim() || undefined,
        trialDays: 14,
        config: {
          permissions: { "*": true },
        },
      };

      const result =
        await subscriptionService.subscribeCompany(subscriptionData);

      if (result.body.success) {
        setSuccess(true);

        // Optimización: Login en segundo plano mientras se muestra éxito
        const loginPromise = !result.body.data?.auth?.token
          ? authAPI
            .login({
              email: formData.adminEmail,
              password: formData.adminPassword,
            })
            .then((loginRes) => {
              if (loginRes.data?.tokens?.access_token) {
                localStorage.setItem(
                  "token",
                  loginRes.data.tokens.access_token,
                );
                if (loginRes.data.tokens.refresh_token) {
                  localStorage.setItem(
                    "refresh_token",
                    loginRes.data.tokens.refresh_token,
                  );
                }
              }
            })
            .catch((e) => console.error("Auto-login failed", e))
          : Promise.resolve();

        // Esperar mínimo 1s para UX y que termine el login
        await Promise.all([
          loginPromise,
          new Promise((resolve) => setTimeout(resolve, 1000)),
        ]);

        const hasToken =
          localStorage.getItem("token") || result.body.data?.auth?.token;

        if (hasToken) {
          if (result.body.data?.auth?.token) {
            localStorage.setItem("token", result.body.data.auth.token);
            localStorage.setItem(
              "refresh_token",
              result.body.data.auth.refresh_token,
            );

            if (result.body.data.user) {
              localStorage.setItem(
                "user",
                JSON.stringify(result.body.data.user),
              );
            }
            if (result.body.data.company) {
              localStorage.setItem(
                "company",
                JSON.stringify(result.body.data.company),
              );
            }
          }

          window.location.hash = "#/dashboard";
        } else {
          navigate("/login", {
            state: {
              message:
                "¡Registro exitoso! Por favor inicia sesión con tus credenciales.",
              email: formData.adminEmail,
            },
          });
        }
      } else {
        throw new Error(
          result.body.message || result.body.error || "Error en el registro",
        );
      }
    } catch (err: any) {
      console.error("❌ Error en registro:", err);
      let errorMessage = "Error en el registro. Por favor intenta nuevamente.";
      if (err.message) errorMessage = err.message;
      setError(errorMessage);
    } finally {
      setRegistering(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen overflow-hidden relative flex items-center justify-center">
        <AnimatedBackground />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative z-10 w-full max-w-md p-8 bg-white/90 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <Check className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            ¡Bienvenido a Kersales!
          </h2>
          <p className="text-slate-600 mb-8 text-lg">
            Tu cuenta ha sido creada. Iniciando sesión automáticamente...
          </p>
          <div className="flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-hidden relative flex items-center justify-center py-6">
      <AnimatedBackground />

      {/* Partículas de fondo flotando (colores del sidebar/login) */}
      {[...Array(12)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: [6, 4, 8, 5, 7, 4, 6, 5, 8, 5, 6, 4][i],
            height: [6, 4, 8, 5, 7, 4, 6, 5, 8, 5, 6, 4][i],
            top: [`10%`, `75%`, `20%`, `85%`, `40%`, `15%`, `60%`, `35%`, `50%`, `90%`, `25%`, `65%`][i],
            left: [`8%`, `12%`, `82%`, `78%`, `5%`, `90%`, `94%`, `45%`, `88%`, `22%`, `35%`, `72%`][i],
            background: i % 2 === 0 ? "rgba(37, 99, 235, 0.25)" : "rgba(124, 58, 237, 0.25)",
            boxShadow: i % 2 === 0 ? "0 0 10px rgba(37, 99, 235, 0.35)" : "0 0 10px rgba(124, 58, 237, 0.35)",
          }}
          animate={{
            y: [0, -25, 0],
            x: [0, [3, -3, 5, -4, 4, -5, 2, -3, 4, -2, 3, -3][i], 0],
            opacity: [0.25, 0.8, 0.25],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: [6, 7, 5, 8, 6.5, 7.5, 4.5, 6, 5.5, 7, 6.2, 5.8][i],
            repeat: Infinity,
            ease: "easeInOut",
            delay: [0, 1.2, 0.4, 2.2, 1.5, 0.8, 2.8, 1.1, 0.5, 1.9, 1.1, 0.3][i],
          }}
        />
      ))}

      <div className="relative z-10 flex items-center justify-center px-4 py-8 w-full max-w-[1140px]">
        {/* ── TARJETA DEL REGISTRO MÁS GRANDE Y ESTILIZADA (TIPO MODAL) ── Centrada */}
        <motion.div
          className="w-full max-w-[590px] bg-white/80 backdrop-blur-md rounded-3xl border border-slate-100/80 shadow-[0_20px_50px_rgba(0,0,0,0.05)] p-6 sm:p-8 relative z-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
        >
          <div className="w-full">
            {/* Cabecera del formulario */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <Link
                  to="/"
                  className="text-xs font-bold text-indigo-900 hover:text-indigo-950 flex items-center gap-1 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" /> Volver al inicio
                </Link>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Paso {step} de 3
                </span>
              </div>

              <h1 className="text-2xl font-extrabold tracking-tight bg-gradient-to-r from-[#1e1b4b] via-[#312e81] to-[#1e293b] bg-clip-text text-transparent mb-1">
                Crear nueva cuenta
              </h1>
              <p className="text-slate-500 text-xs font-semibold">
                Registra tu organización en Kersales para empezar
              </p>
            </div>

            {/* ── BARRA DE PESTAÑAS (TABS INDICADORAS DE PROGRESO) ── */}
            <div className="flex gap-2 bg-slate-100/50 p-1 rounded-2xl mb-8 border border-slate-200/20">
              {[
                { label: "Empresa", step: 1, icon: Building },
                { label: "Administrador", step: 2, icon: User },
                { label: "Confirmación", step: 3, icon: Check },
              ].map((t) => {
                const Icon = t.icon;
                const isActive = step === t.step;
                const isCompleted = step > t.step;
                return (
                  <div
                    key={t.step}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-xl text-xs font-bold transition-all duration-300 ${isActive
                        ? "bg-gradient-to-r from-[#1e1b4b] to-[#312e81] text-white shadow-[0_4px_12px_rgba(49,46,129,0.15)] scale-[1.01]"
                        : isCompleted
                          ? "bg-indigo-900/10 text-indigo-900"
                          : "bg-transparent text-slate-400"
                      }`}
                  >
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="hidden xs:inline">{t.label}</span>
                  </div>
                );
              })}
            </div>

            {error && (
              <Alert
                variant="destructive"
                className="mb-6 bg-red-50/95 border-red-200/50 text-red-700 py-3 rounded-2xl flex items-start gap-2.5 shadow-sm"
              >
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <AlertDescription className="text-xs font-semibold leading-normal">{error}</AlertDescription>
              </Alert>
            )}

            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Nombre de la Empresa (Ocupa toda la fila) */}
                <div className="space-y-2">
                  <Label className="text-xs font-bold text-slate-500 block">
                    Nombre de la Empresa
                  </Label>
                  <div className="relative">
                    <Building className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                    <Input
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      className="pl-10 bg-white/90 backdrop-blur-sm border-slate-200/80 focus:border-[#312e81] focus:ring-1 focus:ring-[#312e81] h-11 rounded-xl text-sm transition-all shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
                      placeholder="Ej: Mi Empresa S.A."
                    />
                  </div>
                  {formErrors.companyName && (
                    <p className="text-xs text-red-500 font-medium ml-1 mt-1">
                      {formErrors.companyName}
                    </p>
                  )}
                </div>

                {/* Email y Teléfono (Dos columnas en tablet/desktop) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold text-slate-500 block">
                      Email Corporativo
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                      <Input
                        name="companyEmail"
                        value={formData.companyEmail}
                        onChange={handleInputChange}
                        className="pl-10 bg-white/90 backdrop-blur-sm border-slate-200/80 focus:border-[#312e81] focus:ring-1 focus:ring-[#312e81] h-11 rounded-xl text-sm transition-all shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
                        placeholder="contacto@empresa.com"
                      />
                    </div>
                    {formErrors.companyEmail && (
                      <p className="text-xs text-red-500 font-medium ml-1 mt-1">
                        {formErrors.companyEmail}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-bold text-slate-500 block">Teléfono</Label>
                    <div className="flex gap-2 relative">
                      <div ref={companyCountryContainerRef} className="relative w-28 sm:w-32 flex-shrink-0">
                        <Input
                          ref={companyCountryRef}
                          value={companyCountrySearch}
                          onChange={(e) => {
                            setCompanyCountrySearch(e.target.value);
                            setShowCompanyCountryDropdown(true);
                          }}
                          onFocus={() => setShowCompanyCountryDropdown(true)}
                          placeholder="País"
                          className="bg-white/90 backdrop-blur-sm border-slate-200/80 focus:border-[#312e81] focus:ring-1 focus:ring-[#312e81] h-11 rounded-xl text-xs transition-all shadow-[0_2px_8px_rgba(0,0,0,0.02)] px-2 text-center font-bold"
                        />
                        {showCompanyCountryDropdown && (
                          <div className="absolute z-50 bg-white/95 backdrop-blur-md border border-slate-100 mt-1 max-h-48 overflow-y-auto w-64 shadow-lg rounded-xl left-0">
                            {filteredCompanyCountries.map((c) => (
                              <div
                                key={c.code}
                                className="cursor-pointer px-3.5 py-2 hover:bg-slate-50 flex items-center gap-2 text-xs font-semibold text-slate-700"
                                onClick={() => handleSelectCompanyCountry(c)}
                              >
                                <span
                                  className={`fi fi-${c.flagCode} w-4 h-3 rounded-sm flex-shrink-0`}
                                ></span>
                                <span className="truncate">
                                  {c.name} ({c.code})
                                </span>
                              </div>
                            ))}
                            {filteredCompanyCountries.length === 0 && (
                              <div className="px-3 py-2 text-slate-400 text-xs font-medium">
                                No encontrado
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <Input
                        name="companyPhone"
                        value={formData.companyPhone}
                        onChange={handleInputChange}
                        placeholder="Número"
                        className="flex-1 bg-white/90 backdrop-blur-sm border-slate-200/80 focus:border-[#312e81] focus:ring-1 focus:ring-[#312e81] h-11 rounded-xl text-sm transition-all shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
                      />
                    </div>
                    {formErrors.companyPhone && (
                      <p className="text-xs text-red-500 font-medium ml-1 mt-1">
                        {formErrors.companyPhone}
                      </p>
                    )}
                  </div>
                </div>

                {/* Sitio Web y Moneda (Dos columnas en tablet/desktop) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold text-slate-500 block">
                      Sitio Web (Opcional)
                    </Label>
                    <div className="relative">
                      <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                      <Input
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        className="pl-10 bg-white/90 backdrop-blur-sm border-slate-200/80 focus:border-[#312e81] focus:ring-1 focus:ring-[#312e81] h-11 rounded-xl text-sm transition-all shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
                        placeholder="https://empresa.com"
                      />
                    </div>
                    {formErrors.website && (
                      <p className="text-xs text-red-500 font-medium ml-1">
                        {formErrors.website}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-bold text-slate-500 block">Moneda</Label>
                    <div className="relative">
                      <Coins className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                      <Select
                        value={formData.currency}
                        onValueChange={(value) =>
                          setFormData((prev) => ({ ...prev, currency: value }))
                        }
                      >
                        <SelectTrigger className="pl-10 bg-white/90 backdrop-blur-sm border-slate-200/80 focus:border-[#312e81] h-11 rounded-xl text-sm transition-all shadow-[0_2px_8px_rgba(0,0,0,0.02)] text-slate-700 font-semibold">
                          <SelectValue placeholder="Selecciona moneda" />
                        </SelectTrigger>
                        <SelectContent className="bg-white/95 backdrop-blur-md rounded-xl border border-slate-100">
                          {currencyOptions.map((option) => (
                            <SelectItem key={option.name} value={option.symbol} className="font-semibold text-slate-700">
                              {option.name} - {option.symbol}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Nombre y Apellido (Lado a lado) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold text-slate-500 block font-sans">Nombre</Label>
                    <Input
                      name="adminFirstName"
                      value={formData.adminFirstName}
                      onChange={handleInputChange}
                      className="bg-white/90 backdrop-blur-sm border-slate-200/80 focus:border-[#312e81] focus:ring-1 focus:ring-[#312e81] h-11 rounded-xl text-sm transition-all shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
                      placeholder="Juan"
                    />
                    {formErrors.adminFirstName && (
                      <p className="text-xs text-red-500 font-medium ml-1 mt-1">
                        {formErrors.adminFirstName}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold text-slate-500 block font-sans">Apellido</Label>
                    <Input
                      name="adminLastName"
                      value={formData.adminLastName}
                      onChange={handleInputChange}
                      className="bg-white/90 backdrop-blur-sm border-slate-200/80 focus:border-[#312e81] focus:ring-1 focus:ring-[#312e81] h-11 rounded-xl text-sm transition-all shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
                      placeholder="Pérez"
                    />
                    {formErrors.adminLastName && (
                      <p className="text-xs text-red-500 font-medium ml-1">
                        {formErrors.adminLastName}
                      </p>
                    )}
                  </div>
                </div>

                {/* Email Administrador */}
                <div className="space-y-2">
                  <Label className="text-xs font-bold text-slate-500 block font-sans">
                    Email Administrador
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                    <Input
                      name="adminEmail"
                      value={formData.adminEmail}
                      onChange={handleInputChange}
                      className="pl-10 bg-white/90 backdrop-blur-sm border-slate-200/80 focus:border-[#312e81] focus:ring-1 focus:ring-[#312e81] h-11 rounded-xl text-sm transition-all shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
                      placeholder="admin@empresa.com"
                    />
                  </div>
                  {formErrors.adminEmail && (
                    <p className="text-xs text-red-500 font-medium ml-1 mt-1">
                      {formErrors.adminEmail}
                    </p>
                  )}
                </div>

                {/* Contraseña y Confirmar Contraseña (Lado a lado) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold text-slate-500 block font-sans">Contraseña</Label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                      <Input
                        type={showPassword ? "text" : "password"}
                        name="adminPassword"
                        value={formData.adminPassword}
                        onChange={handleInputChange}
                        className="pl-10 pr-10 bg-white/90 backdrop-blur-sm border-slate-200/80 focus:border-[#312e81] focus:ring-1 focus:ring-[#312e81] h-11 rounded-xl text-sm transition-all shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-indigo-900 transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {formErrors.adminPassword && (
                      <p className="text-xs text-red-500 font-medium ml-1 mt-1">
                        {formErrors.adminPassword}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-bold text-slate-500 block font-sans">
                      Confirmar Contraseña
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className="pl-10 pr-10 bg-white/90 backdrop-blur-sm border-slate-200/80 focus:border-[#312e81] focus:ring-1 focus:ring-[#312e81] h-11 rounded-xl text-sm transition-all shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-indigo-900 transition-colors"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {formErrors.confirmPassword && (
                      <p className="text-xs text-red-500 font-medium ml-1 mt-1">
                        {formErrors.confirmPassword}
                      </p>
                    )}
                  </div>
                </div>

                {/* Teléfono Admin */}
                <div className="space-y-2">
                  <Label className="text-xs font-bold text-slate-500 block font-sans">
                    Teléfono Admin (Opcional)
                  </Label>
                  <div className="flex gap-2 relative">
                    <div ref={adminCountryContainerRef} className="relative w-28 sm:w-32 flex-shrink-0">
                      <Input
                        ref={adminCountryRef}
                        value={adminCountrySearch}
                        onChange={(e) => {
                          setAdminCountrySearch(e.target.value);
                          setShowAdminCountryDropdown(true);
                        }}
                        onFocus={() => setShowAdminCountryDropdown(true)}
                        placeholder="País"
                        className="bg-white/90 backdrop-blur-sm border-slate-200/80 focus:border-[#312e81] focus:ring-1 focus:ring-[#312e81] h-11 rounded-xl text-xs transition-all shadow-[0_2px_8px_rgba(0,0,0,0.02)] px-2 text-center font-bold"
                      />
                      {showAdminCountryDropdown && (
                        <div className="absolute z-50 bg-white/95 backdrop-blur-md border border-slate-100 mt-1 max-h-48 overflow-y-auto w-64 shadow-lg rounded-xl left-0">
                          {filteredAdminCountries.map((c) => (
                            <div
                              key={c.code}
                              className="cursor-pointer px-3.5 py-2 hover:bg-slate-50 flex items-center gap-2 text-xs font-semibold text-slate-700"
                              onClick={() => handleSelectAdminCountry(c)}
                            >
                              <span
                                className={`fi fi-${c.flagCode} w-4 h-3 rounded-sm flex-shrink-0`}
                              ></span>
                              <span className="truncate">
                                {c.name} ({c.code})
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <Input
                      name="adminPhone"
                      value={formData.adminPhone}
                      onChange={handleInputChange}
                      placeholder="Número"
                      className="flex-1 bg-white/90 backdrop-blur-sm border-slate-200/80 focus:border-[#312e81] focus:ring-1 focus:ring-[#312e81] h-11 rounded-xl text-sm transition-all shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Tarjeta de Datos de la Empresa */}
                  <div className="bg-slate-50/60 border border-slate-200/20 rounded-2xl p-4.5 flex gap-3.5 items-start">
                    <div className="p-2.5 bg-indigo-50 border border-indigo-100/50 rounded-xl text-indigo-900 z-10 flex-shrink-0">
                      <Building className="h-5 w-5" />
                    </div>
                    <div className="space-y-1 min-w-0">
                      <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Empresa</p>
                      <p className="font-bold text-sm text-slate-800 truncate">
                        {formData.companyName}
                      </p>
                      <p className="text-xs font-semibold text-slate-500 truncate">
                        {formData.companyEmail}
                      </p>
                      {formData.companyPhone && (
                        <p className="text-[11px] font-semibold text-slate-400">
                          Tel: {companyCountryCode} {formData.companyPhone}
                        </p>
                      )}
                      {formData.website && (
                        <p className="text-[11px] font-semibold text-[#312e81] truncate">
                          {formData.website}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Tarjeta de Datos del Administrador */}
                  <div className="bg-slate-50/60 border border-slate-200/20 rounded-2xl p-4.5 flex gap-3.5 items-start">
                    <div className="p-2.5 bg-indigo-50 border border-indigo-100/50 rounded-xl text-indigo-900 z-10 flex-shrink-0">
                      <User className="h-5 w-5" />
                    </div>
                    <div className="space-y-1 min-w-0">
                      <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">Administrador</p>
                      <p className="font-bold text-sm text-slate-800 truncate">
                        {formData.adminFirstName} {formData.adminLastName}
                      </p>
                      <p className="text-xs font-semibold text-slate-500 truncate">
                        {formData.adminEmail}
                      </p>
                      {formData.adminPhone && (
                        <p className="text-[11px] font-semibold text-slate-400">
                          Tel: {adminCountryCode} {formData.adminPhone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="text-[10px] font-semibold text-center text-slate-500 leading-relaxed max-w-[340px] mx-auto">
                  Al hacer clic en "Completar Registro", aceptas nuestros{" "}
                  <a href="#" className="text-[#312e81] hover:underline font-bold">
                    Términos de Servicio
                  </a>{" "}
                  y{" "}
                  <a href="#" className="text-[#312e81] hover:underline font-bold">
                    Política de Privacidad
                  </a>
                  .
                </div>
              </motion.div>
            )}

            <div className="mt-6 flex gap-3">
              {step > 1 && (
                <Button
                  variant="outline"
                  onClick={handlePreviousStep}
                  className="flex-1 py-5 rounded-2xl border-slate-200 text-slate-700 font-bold text-xs transition-colors hover:bg-slate-50/50"
                  disabled={registering}
                >
                  Atrás
                </Button>
              )}
              <Button
                onClick={handleNextStep}
                className={`flex-1 py-5 rounded-2xl text-xs font-bold text-white relative overflow-hidden transition-all shadow-[0_4px_14px_rgba(49,46,129,0.25)] ${step === 1 ? "w-full" : ""
                  }`}
                style={{
                  background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)",
                }}
                disabled={registering}
              >
                <motion.div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%)",
                  }}
                  animate={{ x: ["-100%", "200%"] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: "linear" }}
                />
                {registering ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Registrando...
                  </>
                ) : step === 3 ? (
                  <>
                    Completar Registro <Check className="h-4 w-4 ml-1.5" />
                  </>
                ) : (
                  <>
                    Continuar <ArrowRight className="h-4 w-4 ml-1.5" />
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="text-center mt-6">
            <p className="text-[10px] font-semibold flex items-center justify-center text-slate-400">
              <Shield className="h-3.5 w-3.5 mr-1 text-[#312e81]" />
              Tus datos están protegidos y encriptados en Kersales
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PlanRegistrationPage;
